import moment from 'moment';

/** Role names that should show welder ID / station (matches "Welder", "Lead Welder", etc.). */
export const isWelderJobDescription = (text) =>
  String(text || '').trim().toLowerCase().includes('weld');

export const buildOffshoreDisplayName = (firstName, lastName, fallbackName = '') => {
  const name = `${String(firstName || '').trim()} ${String(lastName || '').trim()}`.trim();
  return name || String(fallbackName || '').trim();
};

export const normalizeRosterShift = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const upper = s.toUpperCase();
  if (upper === 'DAY') return 'Day';
  if (upper === 'NIGHT') return 'Night';
  if (upper === '6-6' || upper === '6 - 6' || upper === '6–6') return '6-6';
  if (upper === '8-8' || upper === '8 - 8' || upper === '8–8') return '8-8';
  if (s === 'Day' || s === 'Night' || s === '6-6' || s === '8-8') return s;
  return s;
};

export const parseOnboardValue = (raw) => {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return null;
  if (['yes', 'y', 'true', '1'].includes(s)) return true;
  if (['no', 'n', 'false', '0'].includes(s)) return false;
  return null;
};

export const formatOnboardLabel = (onboard) => {
  if (onboard === true) return 'Yes';
  if (onboard === false) return 'No';
  return '';
};

const pickStationString = (...candidates) => {
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    const s = String(c).trim();
    if (s !== '') return s;
  }
  return '';
};

/**
 * Map API personnel entry → offshore table row.
 */
export const mapOffshorePersonnelEntryToRow = (entry, index, dprShift) => {
  const firstName = entry?.firstName ?? '';
  const lastName = entry?.lastName ?? '';
  const jobDescription =
    entry?.jobDescription ??
    entry?.role?.roleName ??
    entry?.category ??
    '';
  const rawStation = pickStationString(
    entry?.allocatedStation,
    entry?.station,
    entry?.stationNumber,
    entry?.allocated_station
  );

  return {
    id: entry?.id ?? `offshore-${Date.now()}-${index}`,
    rosterId: entry?.rosterId ?? entry?.displayOrder ?? index + 1,
    firstName,
    lastName,
    jobDescription,
    nationality: entry?.nationality ?? '',
    onboard: entry?.onboard ?? null,
    rosterShift: normalizeRosterShift(entry?.shift ?? entry?.rosterShift ?? ''),
    name: buildOffshoreDisplayName(firstName, lastName, entry?.name),
    role: entry?.role?.roleName ?? '',
    planned: dprShift === 'day' ? (entry?.plannedDayShift || 0) : (entry?.plannedNightShift || 0),
    actual: dprShift === 'day' ? (entry?.actualDayShift || 0) : (entry?.actualNightShift || 0),
    userId: entry?.userId,
    roleId: entry?.role?.roleId,
    welderId: entry?.welderId ?? null,
    allocatedStation: rawStation,
    originalEntry: entry,
  };
};

/** Copy roster fields from prior log entries; zero hours for the new day. */
export const copyOffshoreRosterEntries = (entries, dprShift) =>
  (entries || []).map((entry, index) => {
    const rosterOnly = {
      ...entry,
      id: undefined,
      plannedDayShift: 0,
      plannedNightShift: 0,
      actualDayShift: 0,
      actualNightShift: 0,
    };
    const row = mapOffshorePersonnelEntryToRow(rosterOnly, index, dprShift);
    return {
      ...row,
      id: `roster-copy-${Date.now()}-${index}`,
      planned: 0,
      actual: 0,
      isNew: true,
      originalEntry: {
        ...rosterOnly,
        plannedDayShift: 0,
        plannedNightShift: 0,
        actualDayShift: 0,
        actualNightShift: 0,
      },
    };
  });

export const extractDprPersonnelResponse = (responseData) => {
  let personnelEntries = null;
  let remarksData = null;
  let rolesData = null;
  let totalPlanned = null;
  let totalActual = null;
  let isOffshoreProjectFlag = null;

  if (!responseData) {
    return {
      personnelEntries,
      remarksData,
      rolesData,
      totalPlanned,
      totalActual,
      notes: null,
      isOffshoreProject: isOffshoreProjectFlag,
    };
  }

  if (typeof responseData.isOffshoreProject === 'boolean') {
    isOffshoreProjectFlag = responseData.isOffshoreProject;
  } else if (typeof responseData.data?.isOffshoreProject === 'boolean') {
    isOffshoreProjectFlag = responseData.data.isOffshoreProject;
  }

  if (responseData.personnelEntries) {
    personnelEntries = responseData.personnelEntries;
    remarksData = responseData.remarks;
    rolesData = responseData.availableRoles;
    totalPlanned = responseData.totalPlanned;
    totalActual = responseData.totalActual;
  } else if (responseData.data?.personnelEntries) {
    personnelEntries = responseData.data.personnelEntries;
    remarksData = responseData.data.remarks;
    rolesData = responseData.data.availableRoles;
    totalPlanned = responseData.data.totalPlanned;
    totalActual = responseData.data.totalActual;
  } else if (Array.isArray(responseData)) {
    personnelEntries = responseData;
  } else if (Array.isArray(responseData.data)) {
    personnelEntries = responseData.data;
  }

  if (!rolesData && responseData.availableRoles) {
    rolesData = responseData.availableRoles;
  }
  if (totalPlanned === null && responseData.totalPlanned !== undefined) {
    totalPlanned = responseData.totalPlanned;
  }
  if (totalActual === null && responseData.totalActual !== undefined) {
    totalActual = responseData.totalActual;
  }

  let notes = null;
  if (remarksData !== undefined && remarksData !== null) {
    notes = remarksData;
  } else if (responseData.notes !== undefined && responseData.notes !== null) {
    notes = responseData.notes;
  } else if (responseData.data?.notes !== undefined && responseData.data?.notes !== null) {
    notes = responseData.data.notes;
  } else if (responseData.dprPersonnel?.notes !== undefined && responseData.dprPersonnel?.notes !== null) {
    notes = responseData.dprPersonnel.notes;
  }

  return {
    personnelEntries,
    remarksData,
    rolesData,
    totalPlanned,
    totalActual,
    notes,
    isOffshoreProject: isOffshoreProjectFlag,
  };
};

export const extractDprLogIdFromResponse = (responseData) => {
  if (!responseData) return null;
  if (Array.isArray(responseData)) {
    return responseData[0]?.id ?? null;
  }
  if (responseData.id != null) return responseData.id;
  const nested =
    responseData.dprLogs ??
    responseData.logs ??
    (Array.isArray(responseData.data) ? responseData.data : null);
  if (Array.isArray(nested) && nested.length > 0) {
    return nested[0]?.id ?? null;
  }
  return responseData.data?.id ?? null;
};

export const getPreviousLogDateKey = (logDate) => {
  const m = moment(logDate, ['YYYY-MM-DD', moment.ISO_8601], true);
  if (!m.isValid()) return null;
  return m.subtract(1, 'day').format('YYYY-MM-DD');
};

/** Build offshore-specific fields for PUT /dpr-personnel payload. */
export const buildOffshorePersonnelEntryFields = (row, roleObj) => {
  const firstName = String(row.firstName ?? '').trim();
  const lastName = String(row.lastName ?? '').trim();
  const jobDescription = String(row.jobDescription ?? row.role ?? '').trim();
  const name = buildOffshoreDisplayName(firstName, lastName, row.name);

  const fields = {
    rosterId: row.rosterId ?? null,
    firstName,
    lastName,
    jobDescription,
    nationality: String(row.nationality ?? '').trim() || null,
    onboard: row.onboard === true ? true : row.onboard === false ? false : null,
    shift: normalizeRosterShift(row.rosterShift) || null,
    name,
  };

  if (roleObj?.roleName) {
    fields.role = {
      roleId: roleObj.roleId ?? null,
      roleName: roleObj.roleName,
    };
  }

  return fields;
};

export const OFFSHORE_ROSTER_SHIFT_OPTIONS = [
  { value: 'Day', label: 'Day' },
  { value: 'Night', label: 'Night' },
  { value: '6-6', label: '6-6' },
  { value: '8-8', label: '8-8' },
];

/** Include saved/API shift values that are not in the default list. */
export const getOffshoreShiftSelectOptions = (currentShift) => {
  const normalized = normalizeRosterShift(currentShift);
  const options = [...OFFSHORE_ROSTER_SHIFT_OPTIONS];
  if (normalized && !options.some((opt) => opt.value === normalized)) {
    options.push({ value: normalized, label: normalized });
  }
  return options;
};

export const OFFSHORE_ONBOARD_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

export const OFFSHORE_EXCEL_HEADERS = [
  'ID',
  'JOB DESCRIPTION',
  'LAST NAME',
  'FIRST NAME',
  'NATIONALITY',
  'ONBOARD',
  'SHIFT',
  'STATION',
];
